import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getUserData } from "@/lib/api";
import { Navigation } from "@/components/Navigation";
import { DnaBackground } from "@/components/DnaBackground";
import PersonalizedPlanGenerator from "@/components/PersonalizedPlanGenerator";
import PlanCard from "@/components/PlanCard";
import PlanDetailsModal from "@/components/PlanDetailsModal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Target, Sparkles, Loader2 } from "lucide-react";
import { usePlans } from "@/hooks/use-plans";
import { Plan } from "@/lib/api";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

export default function Plans() {
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState<any>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const isRtl = i18n.language === 'ar';
  
  // Fetch plans using React Query
  const { data: plans, isLoading, error } = usePlans();

  useEffect(() => {
    const userData = getUserData();
    if (!userData) {
      navigate("/auth");
    } else {
      setUser(userData);
    }
  }, [navigate]);

  const handleViewPlanDetails = (plan: Plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  if (!user) return null;

  // Mock top strengths - TODO: Replace with actual user data
  const mockTopStrengths = [
    { rank: 1, name: "Achiever", domain: "Executing" },
    { rank: 2, name: "Learner", domain: "Strategic Thinking" },
    { rank: 3, name: "Strategic", domain: "Strategic Thinking" },
    { rank: 4, name: "Empathy", domain: "Relationship Building" },
    { rank: 5, name: "Ideation", domain: "Strategic Thinking" },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden">
      <DnaBackground />
      <Navigation user={user} />
      
      <main className="pt-20 px-4 pb-10 sm:px-6 sm:pt-24 sm:pb-12" dir={isRtl ? "rtl" : "ltr"}>
        <div className="container mx-auto max-w-7xl min-w-0">
          <div className="flex flex-col gap-4 mb-8 animate-fade-in sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-3xl font-bold mb-2 sm:text-4xl">{t('plans.title')}</h1>
              <p className="text-base text-muted-foreground sm:text-xl">
                {t('plans.subtitle')}
              </p>
            </div>
          </div>

          <Tabs defaultValue="myplans" className="min-w-0 space-y-6" dir={isRtl ? "rtl" : "ltr"}>
            <div className="-mx-4 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
            <TabsList className="w-max min-w-full bg-card border border-border sm:min-w-0">
              <TabsTrigger value="myplans" className="gap-2">
                <Target className="w-4 h-4" />
                {t('plans.myPlans')}
              </TabsTrigger>
              <TabsTrigger value="generator" className="gap-2">
                <Sparkles className="w-4 h-4" />
                {t('plans.aiGenerator')}
              </TabsTrigger>
            </TabsList>
            </div>

            <TabsContent value="myplans" className="space-y-6">
              {/* Loading State */}
              {isLoading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="text-center py-12">
                  <p className="text-destructive mb-2">{t('common.error')}</p>
                  <p className="text-sm text-muted-foreground">
                    {getLocalizedErrorMessage(error, 'plans.loadError')}
                  </p>
                </div>
              )}

              {/* Empty State */}
              {!isLoading && !error && (!plans || plans.length === 0) && (
                <div className="text-center py-12">
                  <Target className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold mb-2">{t('plans.noPlans')}</h3>
                  <p className="text-muted-foreground">
                    {t('plans.noPlansDesc')}
                  </p>
                </div>
              )}

              {/* Plans Grid */}
              {!isLoading && !error && plans && plans.length > 0 && (
                <div className="grid gap-6">
                  {plans.map((plan, index) => (
                    <PlanCard
                      key={plan._id}
                      plan={plan}
                      onViewDetails={handleViewPlanDetails}
                      index={index}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="generator">
              <PersonalizedPlanGenerator
                topStrengths={mockTopStrengths}
                userId={user.id}
                planDuration={6}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Plan Details Modal */}
      <PlanDetailsModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        plan={selectedPlan}
      />
    </div>
  );
}
